var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useState } from 'react';
import { apiCreateResourceUpload, apiCreateResourceUploadToStorage, apiUpdateResourceInspect, } from '@/actions/FileActionsTs';
import { addToastId } from '@/actions/ToastActions';
import { notifyUploading, updateNotifyUploading } from '@/helpers/action';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { sleep } from '@/utils/async/sleep';
import { RESOURCE_STATUS_CODE_BEFORE_PROCESS, RESOURCE_STATUS_CODE_PROCESSING, RESOURCE_STATUS_CODE_SUCCESS, } from '@/utils/constants/api/resourceStatusCode';
const fileCheckTimeoutErrorMessage = 'ファイルチェック処理がタイムアウトしました。ファイルを一旦削除して再アップロードするか、しばらく時間を置いてからやり直すか、それでも解決しなければ管理者にお問い合わせください。';
/**
 * ファイルチェックを行うセキュアなファイルアップロード用のカスタムフック
 * @description ユーザーがアップロードしたファイルはS3にアップロードした後、サーバーサイドでファイルチェックを行う。そのため、ファイルチェックが完了するまでの間、ファイルのアップロード状況をポーリングで確認した上で結果を返す
 * @param args
 */
export const useResourceUpload = (args) => {
    const { dispatch, inspectTimeout = 180000, unmountRef } = args;
    const [uploadingCount, changeUploadingCount] = useState(0);
    // 既存で使ってる場所があるので現状維持…
    const uploadFile = useCallback((file, fileType, inspectErrorCallback, unknownErrorCallback) => __awaiter(void 0, void 0, void 0, function* () {
        let result = null;
        changeUploadingCount((prev) => prev + 1);
        try {
            // アップロード先URLの取得
            const uploadUrlResponse = yield wrapDispatch(dispatch, apiCreateResourceUpload({
                json: {
                    file_name: file.name,
                    file_type: fileType,
                },
            }, {}));
            const { content_type: contentType, id, url } = uploadUrlResponse.payload;
            if (unmountRef.current) {
                return null;
            }
            // S3にファイルアップロード
            const uploadAction = apiCreateResourceUploadToStorage({ contentType, file, url }, {});
            yield new Promise((resolve, reject) => {
                dispatch(Object.assign(Object.assign({}, uploadAction), { meta: Object.assign(Object.assign({}, uploadAction.meta), { reject,
                        resolve }) }));
            });
            if (unmountRef.current) {
                return null;
            }
            const startTime = Date.now();
            // ファイルチェックのステータス
            const doingStatus = [
                // 実行中
                RESOURCE_STATUS_CODE_PROCESSING,
                // 実行前
                RESOURCE_STATUS_CODE_BEFORE_PROCESS,
            ];
            // ファイルのチェック処理が終わるのを待つ
            while (!result) {
                // eslint-disable-next-line no-await-in-loop
                yield sleep(5000);
                // 要素がアンマウントされている場合は処理を中断する
                if (unmountRef.current) {
                    return null;
                }
                // eslint-disable-next-line no-await-in-loop
                const { payload: statusResponse } = yield wrapDispatch(dispatch, apiUpdateResourceInspect({
                    path: { id },
                }, {}));
                const isTimeout = Date.now() - startTime > inspectTimeout;
                const { status } = statusResponse;
                if (status === RESOURCE_STATUS_CODE_SUCCESS) {
                    // ファイルチェックが正常終了している
                    result = statusResponse;
                }
                else if (isTimeout) {
                    // 処理がタイムアウトした場合はエラー
                    inspectErrorCallback(Object.assign(Object.assign({}, statusResponse), { message: fileCheckTimeoutErrorMessage }));
                    return null;
                }
                else if (!doingStatus.includes(status)) {
                    // 実行終了しておらず実行もしていない場合はエラー
                    inspectErrorCallback(statusResponse);
                    return null;
                }
            }
        }
        catch (error) {
            unknownErrorCallback(error);
        }
        finally {
            if (!unmountRef.current) {
                changeUploadingCount((prev) => prev - 1);
            }
        }
        return result;
    }), [dispatch, unmountRef, inspectTimeout]);
    const uploadFileToStorage = useCallback((file, fileType) => __awaiter(void 0, void 0, void 0, function* () {
        // アップロードプログレス表示制御用のID
        let toastId = null;
        changeUploadingCount((prev) => prev + 1);
        try {
            // アップロード先URLの取得
            const uploadUrlResponse = yield wrapDispatch(dispatch, apiCreateResourceUpload({
                json: {
                    file_name: file.name,
                    file_type: fileType,
                },
            }, {}));
            if (unmountRef.current) {
                return null;
            }
            // S3にファイルアップロード
            const uploadAction = apiCreateResourceUploadToStorage({
                contentType: uploadUrlResponse.payload.content_type,
                file,
                url: uploadUrlResponse.payload.url,
            }, {
                wrapOnUploadProgress: (progress) => {
                    // アップロードプログレスバー非表示、もしくはunmount状態であれば何もしない
                    if (unmountRef && unmountRef.current) {
                        return;
                    }
                    if (toastId === null) {
                        // アップロードプログレスバーを表示
                        toastId = notifyUploading(progress);
                        dispatch(addToastId(toastId));
                    }
                    else {
                        // 表示中のアップロードプログレスバーを更新
                        updateNotifyUploading(toastId, progress);
                    }
                },
            });
            yield new Promise((resolve, reject) => {
                dispatch(Object.assign(Object.assign({}, uploadAction), { meta: Object.assign(Object.assign({}, uploadAction.meta), { reject,
                        resolve }) }));
            });
            return uploadUrlResponse.payload;
        }
        finally {
            if (!unmountRef.current) {
                changeUploadingCount((prev) => prev - 1);
            }
        }
    }), [dispatch, unmountRef]);
    const inspectFileStatus = useCallback((id, isCancelRef) => __awaiter(void 0, void 0, void 0, function* () {
        let result;
        const startTime = Date.now();
        // ファイルチェックのステータス
        const doingStatus = [
            // 実行中
            RESOURCE_STATUS_CODE_PROCESSING,
            // 実行前
            RESOURCE_STATUS_CODE_BEFORE_PROCESS,
        ];
        // ファイルのチェック処理が終わるのを待つ
        while (!result) {
            if (unmountRef.current || (isCancelRef === null || isCancelRef === void 0 ? void 0 : isCancelRef.current)) {
                return { message: 'キャンセルされました', result: false };
            }
            // eslint-disable-next-line no-await-in-loop
            const { payload: statusResponse } = yield wrapDispatch(dispatch, apiUpdateResourceInspect({
                path: { id },
            }, {}));
            const isTimeout = Date.now() - startTime > inspectTimeout;
            const { message, status } = statusResponse;
            if (status === RESOURCE_STATUS_CODE_SUCCESS) {
                // ファイルチェックが正常終了している
                result = { message, result: true };
            }
            else if (isTimeout) {
                // 処理がタイムアウトした場合はエラー
                result = { message: fileCheckTimeoutErrorMessage, result: false };
            }
            else if (!doingStatus.includes(status)) {
                // 実行終了しておらず実行もしていない場合はエラー
                result = { message, result: false };
            }
            else {
                // eslint-disable-next-line no-await-in-loop
                yield sleep(5000);
            }
        }
        return result;
    }), [dispatch, unmountRef, inspectTimeout]);
    return {
        inspectFileStatus,
        isUploading: uploadingCount > 0,
        uploadFile,
        uploadFileToStorage,
        uploadingCount,
    };
};
